import { IncidenciaDeEtapa } from "./etapas-versiones.model";

/*
 * Las incidencias levantadas en una ejecución de pruebas (T2; plan
 * docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §5.7 y §8):
 * funciones puras para «Vincular incidencia existente» (CONSINCXFILTROQA,
 * el filtro del tablero de QA), «Crear incidencia» (guardarIncidenciaQA.do,
 * el alta de siempre) y los chips de estatus y prioridad de A2 y T2. Que cada
 * id sea del proyecto, de la etapa y del tenant lo valida el servidor al
 * guardar la ejecución (VAL-PRU-INCIDENCIA).
 */

/** Cuántas incidencias de cada estatus pide «Vincular» (regXPagina del filtro de QA). */
export const MAXIMO_POR_ESTATUS = 200;
/** Topes del alta de incidencias (alta-incidencia.component.html). */
export const LARGO_TITULO_INCIDENCIA = 150;
export const LARGO_DESCRIPCION_INCIDENCIA = 2000;
/** El estatus con que nace una incidencia (IncidenciaQABuss.guardaIncidenciaQA lo fija igual). */
export const ESTATUS_ALTA = "ABIERTO";

/** Las que ya no cuentan como abiertas (D1). */
const ESTATUS_NO_ABIERTOS: ReadonlySet<string> = new Set(["VOBO", "CANCELADO"]);

/** Los grupos de IncidenciasDTO en el orden del tablero, con su total. */
const GRUPOS: readonly (readonly [string, string])[] = [
  ["listaIncAbierto", "totalItemsAbierto"],
  ["listaIncObservacion", "totalItemsObservacion"],
  ["listaIncProceso", "totalItemsProceso"],
  ["listaIncResuelto", "totalItemsResuelto"],
  ["listaIncRevision", "totalItemsRevision"],
  ["listaIncVOBO", "totalItemsVOBO"],
  ["listaIncCancelado", "totalItemsCancelado"],
];

/** Cómo se filtra «Vincular incidencia existente» (maqueta T2v). */
export type FiltroVincular = "version" | "abiertas" | "todas";

export const FILTROS_VINCULAR: readonly { clave: FiltroVincular; texto: string }[] = [
  { clave: "version", texto: "Detectadas en esta versión" },
  { clave: "abiertas", texto: "Abiertas de la etapa" },
  { clave: "todas", texto: "Todas de la etapa" },
];

/** Una incidencia como la manda el filtro de QA o el alta (IncidenciaBean, solo lo que se lee). */
export interface IncidenciaBeanLeida {
  id?: number | null;
  id_incidencia_proyecto?: number | null;
  titulo?: string | null;
  prioridad?: string | null;
  estatus?: string | null;
  version?: string | null;
}

/** La respuesta de CONSINCXFILTROQA: los grupos por estatus vienen en bean2.incDTO. */
export interface ConsultaIncidenciasEtapa {
  pCodRetorno?: string | null;
  pMensaje?: string | null;
  bean2?: { incDTO?: Record<string, unknown> | null } | null;
}

/** Lo que se pudo leer: las incidencias, si faltaron algunas (más de 200 de un estatus) y el error. */
export interface IncidenciasDeLaConsulta {
  incidencias: IncidenciaDeEtapa[];
  incompleta: boolean;
  /** null si se leyó; el texto para el usuario si no. */
  error: string | null;
}

/** El cuerpo de guardarIncidenciaQA.do (IncidenciaBean) que manda «Crear incidencia». */
export interface IncidenciaNueva {
  titulo: string;
  descincidencia: string;
  estatus: string;
  prioridad: string;
  tipo: string;
  id_proyecto: string;
  id_etapa: string;
  /** La versión que se está probando: la detectada. */
  version: string;
}

/** La respuesta del alta: la incidencia con su id, su folio y el id de su primer detalle (para la evidencia). */
export interface RespuestaAltaIncidencia extends IncidenciaBeanLeida {
  id_proyecto?: string | null;
  detalleBean?: { id?: number | null } | null;
  pCodRetorno?: string | null;
  pMensaje?: string | null;
}

/** Lo capturado en «Crear incidencia». */
export interface DatosIncidenciaNueva {
  titulo: string | null;
  prioridad: string | null;
  tipo: string | null;
  descripcion: string | null;
}

/* ============================ Vincular (CONSINCXFILTROQA) ============================ */

/**
 * El cuerpo para CONSINCXFILTROQA con proyecto y etapa, sin filtros de
 * texto, prioridad, tipo ni fechas y con todos los estatus (listas vacías =
 * todos, como el tablero); el tenant lo pone el servidor.
 */
export function consultaIncidenciasDeEtapa(idProyecto: string, idEtapa: string): object {
  return {
    bean: {
      id_proyecto: idProyecto,
      id_etapa: idEtapa,
      estatus: null,
      titulo: null,
      numeroInci: null,
      fec_ini: null,
      fec_fin: null,
      milistaprioridad: [],
      milistatipo: [],
      milistaestatus: [],
    },
    regInicial: 0,
    regXPagina: MAXIMO_POR_ESTATUS,
  };
}

/**
 * Las incidencias de la respuesta del filtro de QA, en el orden del tablero
 * y sin repetidas. Sin bean2.incDTO (el servidor falló o rechazó) da el
 * error; si algún estatus tiene más de las que vinieron, incompleta.
 */
export function incidenciasDeLaConsulta(respuesta: ConsultaIncidenciasEtapa | null | undefined): IncidenciasDeLaConsulta {
  const grupos = respuesta?.bean2?.incDTO;
  if (!grupos) {
    const texto = respuesta?.pMensaje?.trim() || "No se pudieron consultar las incidencias de la etapa.";
    return { incidencias: [], incompleta: false, error: texto };
  }
  const vistas = new Set<number>();
  const incidencias: IncidenciaDeEtapa[] = [];
  let incompleta = false;
  for (const [lista, total] of GRUPOS) {
    const filas = Array.isArray(grupos[lista]) ? (grupos[lista] as IncidenciaBeanLeida[]) : [];
    const cuantas = typeof grupos[total] === "number" ? (grupos[total] as number) : filas.length;
    incompleta = incompleta || cuantas > filas.length;
    for (const fila of filas) {
      const incidencia = incidenciaDeBean(fila);
      if (incidencia && !vistas.has(incidencia.id)) {
        vistas.add(incidencia.id);
        incidencias.push(incidencia);
      }
    }
  }
  return { incidencias, incompleta, error: null };
}

/** La incidencia de un IncidenciaBean; null sin id. */
export function incidenciaDeBean(bean: IncidenciaBeanLeida | null | undefined): IncidenciaDeEtapa | null {
  if (typeof bean?.id !== "number") {
    return null;
  }
  return {
    id: bean.id,
    folio: typeof bean.id_incidencia_proyecto === "number" ? bean.id_incidencia_proyecto : null,
    titulo: bean.titulo ?? null,
    prioridad: bean.prioridad ?? null,
    estatus: bean.estatus ?? null,
    version: bean.version ?? null,
  };
}

/** D1: abierta es cualquier estatus salvo VOBO y CANCELADO. */
export function esAbierta(estatus: string | null | undefined): boolean {
  return !ESTATUS_NO_ABIERTOS.has((estatus ?? "").trim().toUpperCase());
}

/**
 * Las incidencias que muestra «Vincular» con el filtro y la búsqueda: por
 * folio («33» o «#33», exacto) o por título (sin distinguir mayúsculas ni
 * acentos). Las más recientes (folio mayor) primero.
 */
export function filtrarParaVincular(
  incidencias: readonly IncidenciaDeEtapa[],
  filtro: FiltroVincular,
  idVersion: string | null | undefined,
  busqueda: string | null | undefined,
): IncidenciaDeEtapa[] {
  const texto = normalizar(busqueda);
  const folio = /^#?(\d+)$/.exec(texto);
  return incidencias
    .filter((incidencia) => cumpleFiltro(incidencia, filtro, idVersion))
    .filter((incidencia) => {
      if (!texto) {
        return true;
      }
      return folio ? incidencia.folio === Number(folio[1]) : normalizar(incidencia.titulo).includes(texto);
    })
    .sort((a, b) => (b.folio ?? 0) - (a.folio ?? 0));
}

function cumpleFiltro(incidencia: IncidenciaDeEtapa, filtro: FiltroVincular, idVersion: string | null | undefined): boolean {
  if (filtro === "version") {
    return !!idVersion && incidencia.version === idVersion;
  }
  return filtro === "todas" || esAbierta(incidencia.estatus);
}

/** «#33», «#33 y #35», «#31, #33 y #35». */
export function textoFolios(folios: readonly (number | null)[]): string {
  const textos = folios.map((folio) => (folio === null ? "sin folio" : `#${folio}`));
  if (textos.length <= 1) {
    return textos[0] ?? "";
  }
  return `${textos.slice(0, -1).join(", ")} y ${textos.at(-1)}`;
}

/* ============================ Crear incidencia ============================ */

/** El cuerpo del alta con lo capturado: textos recortados, en ABIERTO y con la versión que se está probando. */
export function incidenciaNueva(
  datos: DatosIncidenciaNueva,
  claves: { idProyecto: string; idEtapa: string; idVersion: string },
): IncidenciaNueva {
  return {
    titulo: (datos.titulo ?? "").trim(),
    descincidencia: (datos.descripcion ?? "").trim(),
    estatus: ESTATUS_ALTA,
    prioridad: datos.prioridad ?? "",
    tipo: datos.tipo ?? "",
    id_proyecto: claves.idProyecto,
    id_etapa: claves.idEtapa,
    version: claves.idVersion,
  };
}

/**
 * Por qué no se creó la incidencia; null si se creó. Un rechazo del alta
 * (VAL-INC-VERSION) viaja en el cuerpo; una falla del servidor llega sin
 * cuerpo o sin id.
 */
export function rechazoDelAlta(respuesta: RespuestaAltaIncidencia | null | undefined): string | null {
  const codigo = (respuesta?.pCodRetorno ?? "").trim();
  if (codigo.startsWith("VAL-")) {
    return respuesta?.pMensaje?.trim() || "El servidor rechazó la incidencia. No se guardó nada.";
  }
  return typeof respuesta?.id === "number" ? null : "No se pudo crear la incidencia: el servidor no la confirmó.";
}

/**
 * El nombre con que se sube cada archivo de evidencia de la incidencia
 * nueva, como el alta: INCIDENCIA$QA$id$detalle$proyecto$nombre (lo lee
 * uploadEvidencia.do).
 */
export function nombreEvidenciaIncidencia(respuesta: RespuestaAltaIncidencia, nombreArchivo: string): string {
  return ["INCIDENCIA", "QA", respuesta.id, respuesta.detalleBean?.id, respuesta.id_proyecto, nombreArchivo].join("$");
}

/* ============================ Chips ============================ */

/** La clase del chip de estatus (app-chip-estatus en styles.css): «is-abierto», «is-vobo». */
export function claseEstatus(estatus: string | null | undefined): string {
  const limpio = (estatus ?? "").trim().toLowerCase();
  return limpio ? `is-${limpio}` : "";
}

/** La clase del punto de prioridad (app-prioridad): «is-alta», «is-media», «is-baja». */
export function clasePrioridad(prioridad: string | null | undefined): string {
  const limpio = (prioridad ?? "").trim().toLowerCase();
  return limpio ? `is-${limpio}` : "";
}

/** «Alta», «Media», «Baja» (D13: prioridad, no severidad); «—» sin prioridad. */
export function nombrePrioridad(prioridad: string | null | undefined): string {
  const limpio = (prioridad ?? "").trim().toLowerCase();
  return limpio ? limpio.charAt(0).toUpperCase() + limpio.slice(1) : "—";
}

/** Minúsculas, sin acentos ni espacios en los extremos. */
function normalizar(texto: string | null | undefined): string {
  return (texto ?? "")
    .normalize("NFD")
    .replaceAll(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}
